import {
  AppstoreOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  FileTextOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ark/ui/icons";
import type {
  DriveDashboardSummary,
  FederatedAppProps,
  ImDashboardSummary,
} from "@ark/contracts";
import { IM_UNREAD_CHANGED_EVENT } from "@ark/contracts";
import {
  ArkThemeProvider,
  MotionRegion,
  useRuntimeSession,
} from "@ark/ui/primitives";
import { createStyles } from "@ark/ui/styling";
import {
  Button,
  Meter,
  Notice,
  Spinner,
  Statistic,
  Tag,
  Text,
  Title,
} from "@ark/ui/controls";
import { useEffect, useState, type ReactNode } from "react";

import {
  buildContinuationItems,
  buildUpcomingTasks,
  formatContinuationTime,
  formatUpcomingTaskTime,
  greetingForHour,
  type ContinuationItem,
  type ContinuationKind,
  type HomeAIConversationSummary,
  type HomeApprovalSummary,
  type HomeConversationSummary,
  type HomeDocumentSummary,
  type HomeMeetingSummary,
  type HomeScheduledTaskSummary,
  type UpcomingTaskItem,
} from "./homeExperience";
import { imBadge } from "./imUnread";

const useStyles = createStyles(({ css, token }) => {
  const mdMax = `@media (max-width: ${token.screenMDMax}px)`;
  const smMax = `@media (max-width: ${token.screenSMMax}px)`;
  return {
    actionArrow: css`
      color: ${token.colorTextTertiary};
      font-size: 12px;
      margin-left: auto;
      transition: transform var(--ark-motion-feedback) ease;
    `,
    actionCopy: css`
      display: flex;
      flex-direction: column;
      min-width: 0;

      small {
        color: ${token.colorTextTertiary};
        font-size: 11px;
        line-height: 17px;
      }

      strong {
        color: ${token.colorText};
        font-size: 14px;
        font-weight: 650;
        line-height: 20px;
      }
    `,
    actionIcon: css`
      align-items: center;
      background: ${token.colorPrimaryBg};
      border-radius: 9px;
      color: ${token.colorPrimary};
      display: inline-flex;
      flex: 0 0 36px;
      font-size: 17px;
      height: 36px;
      justify-content: center;
      width: 36px;
      position: relative;
    `,
    actionBadge: css`
      align-items: center;
      background: ${token.colorError};
      border: 2px solid ${token.colorBgContainer};
      border-radius: 999px;
      color: ${token.colorTextLightSolid};
      display: inline-flex;
      font-size: 9px;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      height: 17px;
      justify-content: center;
      min-width: 17px;
      padding-inline: 3px;
      position: absolute;
      right: -7px;
      top: -7px;
    `,
    actionBadgeUnknown: css`
      background: ${token.colorFillSecondary};
      color: ${token.colorTextTertiary};
    `,
    alert: css`
      margin-bottom: 12px;
    `,
    eyebrow: css`
      color: ${token.colorPrimary};
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    `,
    emptyContinuations: css`
      align-items: center;
      color: ${token.colorTextTertiary};
      display: flex;
      flex-direction: column;
      gap: 10px;
      justify-content: center;
      min-height: 260px;

      .ark-icon {
        color: ${token.colorSuccess};
        font-size: 28px;
      }
    `,
    focusItem: css`
      align-items: center;
      appearance: none;
      background: transparent;
      border: 0;
      border-top: 1px solid ${token.colorBorderSecondary};
      color: ${token.colorText};
      cursor: pointer;
      display: grid;
      gap: 12px;
      grid-template-columns: auto minmax(0, 1fr) auto;
      min-height: 66px;
      padding: 11px 2px;
      text-align: left;
      transition:
        background var(--ark-motion-feedback) ease,
        padding var(--ark-motion-feedback) ease;
      width: 100%;

      &:hover,
      &:focus-visible {
        background: ${token.colorFillQuaternary};
        border-radius: 8px;
        padding-inline: 10px;
      }
    `,
    focusMeta: css`
      color: ${token.colorTextTertiary};
      display: block;
      font-size: 12px;
      line-height: 18px;
      margin-top: 2px;
    `,
    focusTitle: css`
      display: block;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
    `,
    greeting: css`
      align-items: flex-end;
      display: flex;
      gap: 24px;
      justify-content: space-between;
      margin-bottom: 18px;

      h1 {
        font-size: clamp(25px, 3vw, 32px);
        letter-spacing: -0.035em;
        line-height: 1.2;
        margin: 5px 0 6px;
      }

      p {
        color: ${token.colorTextSecondary};
        font-size: 13px;
        margin: 0;
      }

      ${smMax} {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
      }
    `,
    icon: css`
      align-items: center;
      background: ${token.colorFillQuaternary};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 9px;
      color: ${token.colorPrimary};
      display: inline-flex;
      flex: 0 0 38px;
      font-size: 17px;
      height: 38px;
      justify-content: center;
      width: 38px;
    `,
    metric: css`
      --ark-statistic-label-color: ${token.colorTextTertiary};
      --ark-statistic-label-font-size: 11px;
      --ark-statistic-label-line-height: 16px;
      --ark-statistic-value-font-size: 19px;
      --ark-statistic-value-line-height: 26px;
      --ark-statistic-value-margin: 2px 0 0;

      min-width: 0;
      padding: 13px 14px;

      & + & {
        border-left: 1px solid ${token.colorBorderSecondary};
      }

      .ark-statistic-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ${smMax} {
        & + & {
          border-left: 0;
          border-top: 1px solid ${token.colorBorderSecondary};
        }
      }
    `,
    metrics: css`
      background: ${token.colorFillQuaternary};
      border-radius: 9px;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      margin-top: 16px;

      ${smMax} {
        grid-template-columns: 1fr;
      }
    `,
    panel: css`
      background: var(--ark-fx-surface);
      backdrop-filter: var(--ark-fx-filter);
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 12px;
      box-shadow: var(--ark-fx-shadow);
      min-width: 0;
      padding: 18px;
    `,
    panelHeader: css`
      align-items: center;
      display: flex;
      gap: 16px;
      justify-content: space-between;
      margin-bottom: 10px;

      h2 {
        font-size: 16px;
        margin: 0;
      }
    `,
    quickAction: css`
      align-items: center;
      appearance: none;
      background: ${token.colorBgContainer};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 10px;
      cursor: pointer;
      display: flex;
      gap: 11px;
      min-height: 68px;
      padding: 12px;
      text-align: left;
      transition:
        border-color var(--ark-motion-feedback) ease,
        box-shadow var(--ark-motion-feedback) ease,
        transform var(--ark-motion-feedback) ease;
      width: 100%;

      &:hover,
      &:focus-visible {
        border-color: ${token.colorPrimaryBorder};
        box-shadow: var(--ark-fx-glow);
        transform: translateY(calc(var(--ark-motion-distance) / -6));

        .ark-home-action-arrow {
          transform: translateX(2px);
        }
      }
    `,
    quickGrid: css`
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      margin-bottom: 14px;

      ${mdMax} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      ${smMax} {
        grid-template-columns: 1fr;
      }
    `,
    root: css`
      background-image: var(--ark-fx-aurora);
      height: calc(100dvh - var(--ark-shell-header-height, 0px));
      margin: 0 auto;
      max-width: 1320px;
      overflow: auto;
      padding: 24px 28px 40px;
      width: 100%;

      ${mdMax} {
        height: auto;
        min-height: 100%;
        overflow: visible;
      }

      ${smMax} {
        padding: 18px 12px 28px;
      }
    `,
    sectionCaption: css`
      color: ${token.colorTextTertiary};
      display: block;
      font-size: 12px;
      margin-top: 3px;
    `,
    sideStack: css`
      display: grid;
      gap: 14px;
    `,
    status: css`
      align-items: center;
      color: ${token.colorTextSecondary};
      display: flex;
      font-size: 12px;
      gap: 7px;
      white-space: nowrap;
    `,
    itemStatus: css`
      align-items: center;
      color: ${token.colorPrimary};
      display: inline-flex;
      font-size: 12px;
      font-weight: 600;
      gap: 5px;
      white-space: nowrap;
    `,
    statusDot: css`
      background: ${token.colorSuccess};
      border-radius: 50%;
      box-shadow: 0 0 0 3px ${token.colorSuccessBg};
      height: 7px;
      width: 7px;

      &[data-warning="true"] {
        background: ${token.colorWarning};
        box-shadow: 0 0 0 3px ${token.colorWarningBg};
      }
    `,
    usage: css`
      margin-top: 17px;
    `,
    usageLine: css`
      align-items: baseline;
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;

      span {
        color: ${token.colorTextSecondary};
        font-size: 12px;
      }

      strong {
        font-size: 13px;
        font-variant-numeric: tabular-nums;
      }
    `,
    workspaceGrid: css`
      display: grid;
      gap: 14px;
      grid-template-columns: minmax(0, 1.75fr) minmax(280px, 0.8fr);

      ${mdMax} {
        grid-template-columns: 1fr;
      }
    `,
  };
});

interface DashboardState {
  continuations: ContinuationItem[];
  continuationFailures: number;
  drive: DriveDashboardSummary | undefined;
  failedSections: number;
  im: ImDashboardSummary | undefined;
  loading: boolean;
  taskLoadFailed: boolean;
  upcomingTasks: UpcomingTaskItem[];
  updatedAt?: Date;
}

interface HomePage<T> {
  items: T[];
  total: number;
}

export default function HomeApp({ runtime }: FederatedAppProps) {
  const session = useRuntimeSession(runtime);

  if (!session) return null;
  return (
    <ArkThemeProvider>
      <HomeContent
        key={`${session.tenant.id}:${session.user.id}`}
        runtime={runtime}
        session={session}
      />
    </ArkThemeProvider>
  );
}

function HomeContent({
  runtime,
  session,
}: FederatedAppProps & {
  session: NonNullable<ReturnType<FederatedAppProps["runtime"]["getSession"]>>;
}) {
  const { styles } = useStyles();
  const [revision, setRevision] = useState(0);
  const [dashboard, setDashboard] = useState<DashboardState>({
    continuations: [],
    continuationFailures: 0,
    drive: undefined,
    failedSections: 0,
    im: undefined,
    loading: true,
    taskLoadFailed: false,
    upcomingTasks: [],
  });
  const displayName = session.user.displayName || session.user.name;

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const request = <T,>(path: string) =>
      runtime.api.get<T>(path, { signal: controller.signal });
    void Promise.allSettled([
      request<DriveDashboardSummary>("/drive/dashboard/summary"),
      request<ImDashboardSummary>("/im/dashboard/summary"),
      request<HomePage<HomeConversationSummary>>(
        "/im/conversation/list?page=1&pageSize=1",
      ),
      request<HomePage<HomeDocumentSummary>>(
        "/docs/document/list?page=1&pageSize=1",
      ),
      request<HomePage<HomeApprovalSummary>>(
        "/approvals/mine?page=1&pageSize=1&view=pending&status=pending",
      ),
      request<HomePage<HomeMeetingSummary>>(
        "/meeting/rooms?page=1&pageSize=1&status=active",
      ),
      request<HomePage<HomeAIConversationSummary>>(
        "/ai/conversations?page=1&pageSize=1",
      ),
      request<{ runs: HomeScheduledTaskSummary[] }>(
        "/ai/agent-tasks?limit=100",
      ),
    ]).then(
      ([
        drive,
        im,
        conversations,
        documents,
        approvals,
        meetings,
        ai,
        tasks,
      ]) => {
        if (!alive) return;
        const results = [
          drive,
          im,
          conversations,
          documents,
          approvals,
          meetings,
          ai,
        ];
        const aiPage = settled(ai);
        const approvalPage = settled(approvals);
        const conversationPage = settled(conversations);
        const documentPage = settled(documents);
        const meetingPage = settled(meetings);
        const continuationResults = [
          conversations,
          documents,
          approvals,
          meetings,
          ai,
        ];
        setDashboard({
          continuations: buildContinuationItems({
            ...(aiPage ? { ai: aiPage.items } : {}),
            ...(approvalPage ? { approvals: approvalPage.items } : {}),
            ...(conversationPage
              ? { conversations: conversationPage.items }
              : {}),
            ...(documentPage ? { documents: documentPage.items } : {}),
            ...(meetingPage
              ? {
                  meetings: meetingPage.items.filter(
                    ({ status }) => status === "active",
                  ),
                }
              : {}),
          }),
          continuationFailures: continuationResults.filter(
            ({ status }) => status === "rejected",
          ).length,
          drive: drive.status === "fulfilled" ? drive.value : undefined,
          failedSections: results.filter(({ status }) => status === "rejected")
            .length,
          im: im.status === "fulfilled" ? im.value : undefined,
          loading: false,
          taskLoadFailed: tasks.status === "rejected",
          upcomingTasks:
            tasks.status === "fulfilled"
              ? buildUpcomingTasks(tasks.value.runs ?? [])
              : [],
          updatedAt: new Date(),
        });
      },
    );
    return () => {
      alive = false;
      controller.abort();
    };
  }, [revision, runtime]);

  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const timer = window.setInterval(refreshWhenVisible, 30_000);
    window.addEventListener(IM_UNREAD_CHANGED_EVENT, refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(IM_UNREAD_CHANGED_EVENT, refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  const drive = dashboard.drive;
  const im = dashboard.im;
  const unreadBadge = imBadge(im);
  const usagePercent = percent(drive?.usedBytes, drive?.quotaBytes);

  return (
    <MotionRegion
      className={styles.root}
      role="main"
      transitionKey={session.tenant.id}
    >
      <header className={styles.greeting}>
        <div>
          <Text className={styles.eyebrow}>{todayLabel()} · 工作台</Text>
          <Title level={1}>
            {greetingForHour(new Date().getHours())}，{displayName}
          </Title>
          <p>
            在 {session.tenant.name} 继续今天的协作
            {dashboard.updatedAt
              ? ` · ${formatTime(dashboard.updatedAt)} 已同步`
              : " · 正在同步"}
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={dashboard.loading}
          onClick={() => {
            setDashboard((current) => ({ ...current, loading: true }));
            setRevision((value) => value + 1);
          }}
        >
          刷新工作台
        </Button>
      </header>

      {dashboard.failedSections > 0 ? (
        <Notice
          className={styles.alert}
          title={`${dashboard.failedSections} 项协作数据暂时不可用，其他功能仍可使用`}
          type="warning"
        />
      ) : null}

      <section aria-label="快捷开始" className={styles.quickGrid}>
        <QuickAction
          {...(unreadBadge ? { badge: unreadBadge } : {})}
          icon={<MessageOutlined />}
          label="发消息"
          meta="进入会话列表"
          onClick={() => runtime.navigate("/apps/im")}
        />
        <QuickAction
          icon={<FileTextOutlined />}
          label="打开云文档"
          meta="创建、编辑与协作"
          onClick={() => runtime.navigate("/apps/docs")}
        />
        <QuickAction
          icon={<CloudOutlined />}
          label="团队网盘"
          meta="进入共享文件空间"
          onClick={() => runtime.navigate("/apps/drive?view=team")}
        />
        <QuickAction
          icon={<VideoCameraOutlined />}
          label="进入会议"
          meta="创建会议或继续加入"
          onClick={() => runtime.navigate("/apps/meeting")}
        />
        <QuickAction
          icon={<ClockCircleOutlined />}
          label="新建定时任务"
          meta="按计划自动执行 AI 工作"
          onClick={() => runtime.navigate("/apps/ai/tasks/new")}
        />
      </section>

      <div className={styles.workspaceGrid}>
        <section aria-labelledby="today-focus-title" className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <Title id="today-focus-title" level={2}>
                继续工作
              </Title>
              <Text className={styles.sectionCaption}>
                最近会议、审批、会话与内容集中在这里
              </Text>
            </div>
            <span className={styles.status}>
              <span
                className={styles.statusDot}
                data-warning={dashboard.continuationFailures > 0}
              />
              {dashboard.loading
                ? "正在同步"
                : dashboard.continuationFailures > 0
                  ? `${5 - dashboard.continuationFailures}/5 项已同步`
                  : "续办已同步"}
            </span>
          </div>
          {dashboard.loading ? (
            <div className={styles.emptyContinuations}>
              <Spinner tip="正在同步续办项目" />
            </div>
          ) : dashboard.continuations.length === 0 ? (
            <div className={styles.emptyContinuations}>
              {dashboard.continuationFailures > 0 ? (
                <>
                  <ReloadOutlined />
                  <span>续办数据未完整同步，请刷新重试</span>
                </>
              ) : (
                <>
                  <CheckCircleOutlined />
                  <span>暂时没有需要续办的项目</span>
                </>
              )}
            </div>
          ) : (
            dashboard.continuations.map((item) => (
              <FocusItem
                icon={continuationIcon(item.kind)}
                key={`${item.kind}:${item.id}`}
                meta={`${item.meta} · ${formatContinuationTime(item.updatedAt)}`}
                onClick={() => runtime.navigate(item.path)}
                status={item.status}
                title={item.title}
              />
            ))
          )}
        </section>

        <aside className={styles.sideStack}>
          <section
            aria-labelledby="upcoming-tasks-title"
            className={styles.panel}
          >
            <div className={styles.panelHeader}>
              <div>
                <Title id="upcoming-tasks-title" level={2}>
                  即将执行
                </Title>
                <Text className={styles.sectionCaption}>
                  接下来运行的定时任务
                </Text>
              </div>
              <Button
                onClick={() => runtime.navigate("/apps/ai/tasks")}
                size="small"
                type="link"
              >
                查看全部
              </Button>
            </div>
            {dashboard.loading ? (
              <Spinner tip="正在同步定时任务" />
            ) : dashboard.taskLoadFailed ? (
              <Text type="secondary">定时任务暂时无法同步，不影响其他工作</Text>
            ) : dashboard.upcomingTasks.length === 0 ? (
              <Text type="secondary">暂无即将执行的任务</Text>
            ) : (
              dashboard.upcomingTasks.map((task) => (
                <FocusItem
                  icon={<ClockCircleOutlined />}
                  key={task.id}
                  meta={`${formatUpcomingTaskTime(task.availableAt)} 执行`}
                  onClick={() =>
                    runtime.navigate(
                      `/apps/ai/tasks/${encodeURIComponent(task.id)}`,
                    )
                  }
                  status="查看"
                  title={task.title}
                />
              ))
            )}
            <Button
              block
              icon={<PlusOutlined />}
              onClick={() => runtime.navigate("/apps/ai/tasks/new")}
              size="small"
            >
              新建定时任务
            </Button>
          </section>

          <section
            aria-labelledby="workspace-summary-title"
            className={styles.panel}
          >
            <div className={styles.panelHeader}>
              <div>
                <Title id="workspace-summary-title" level={2}>
                  工作空间
                </Title>
                <Text className={styles.sectionCaption}>
                  次要指标保持紧凑，不打断当前任务
                </Text>
              </div>
              <Tag>工作概览</Tag>
            </div>
            <div className={styles.metrics}>
              <Statistic
                className={styles.metric}
                label="可见空间"
                value={drive ? formatCount(drive.spaceCount) : "—"}
              />
              <Statistic
                className={styles.metric}
                label="有效外链"
                value={drive ? formatCount(drive.activeExternalLinks) : "—"}
              />
              <Statistic
                className={styles.metric}
                label="近 7 日会话"
                value={im ? formatCount(im.activeConversationCount) : "—"}
              />
            </div>
            <div className={styles.usage}>
              <div className={styles.usageLine}>
                <span>团队存储</span>
                <strong>
                  {drive
                    ? `${formatBytes(drive.usedBytes)} / ${formatBytes(drive.quotaBytes)}`
                    : "暂不可用"}
                </strong>
              </div>
              <Meter
                aria-label="团队存储使用率"
                max={100}
                min={0}
                value={usagePercent}
                style={{ width: "100%", height: 10 }}
              >
                {usagePercent}%
              </Meter>
            </div>
          </section>

          <section
            aria-labelledby="common-tools-title"
            className={styles.panel}
          >
            <div className={styles.panelHeader}>
              <div>
                <Title id="common-tools-title" level={2}>
                  常用工具
                </Title>
                <Text className={styles.sectionCaption}>
                  随时回到常用工作区
                </Text>
              </div>
            </div>
            <FocusItem
              icon={<RobotOutlined />}
              meta="继续对话、检索和处理内容"
              onClick={() => runtime.navigate("/apps/ai")}
              status="打开"
              title="AI 助手"
            />
            <FocusItem
              icon={<TeamOutlined />}
              meta="查找同事与组织架构"
              onClick={() => runtime.navigate("/directory")}
              status="打开"
              title="通讯录"
            />
            <FocusItem
              icon={<AppstoreOutlined />}
              meta="浏览当前组织可用应用"
              onClick={() => runtime.navigate("/apps")}
              status="全部应用"
              title="应用中心"
            />
          </section>
        </aside>
      </div>
    </MotionRegion>
  );
}

function QuickAction({
  badge,
  icon,
  label,
  meta,
  onClick,
}: {
  badge?: { label: string; text: string; unknown?: boolean };
  icon: ReactNode;
  label: string;
  meta: string;
  onClick: () => void;
}) {
  const { styles, cx } = useStyles();
  return (
    <Button className={styles.quickAction} onClick={onClick} type="unstyled">
      <span className={styles.actionIcon}>
        {icon}
        {badge ? (
          <span
            aria-label={badge.label}
            className={`${styles.actionBadge} ${badge.unknown ? styles.actionBadgeUnknown : ""}`}
          >
            {badge.text}
          </span>
        ) : null}
      </span>
      <span className={styles.actionCopy}>
        <strong>{label}</strong>
        <small>{meta}</small>
      </span>
      <ArrowRightOutlined
        className={cx(styles.actionArrow, "ark-home-action-arrow")}
      />
    </Button>
  );
}

function FocusItem({
  icon,
  meta,
  onClick,
  status,
  title,
}: {
  icon: ReactNode;
  meta: string;
  onClick: () => void;
  status: string;
  title: string;
}) {
  const { styles } = useStyles();
  return (
    <Button
      aria-label={`${title}：${status}`}
      className={styles.focusItem}
      onClick={onClick}
      type="unstyled"
    >
      <span className={styles.icon}>{icon}</span>
      <span>
        <span className={styles.focusTitle}>{title}</span>
        <span className={styles.focusMeta}>{meta}</span>
      </span>
      <span className={styles.itemStatus}>
        {status}
        <ArrowRightOutlined />
      </span>
    </Button>
  );
}

function continuationIcon(kind: ContinuationKind): ReactNode {
  switch (kind) {
    case "meeting":
      return <VideoCameraOutlined />;
    case "approval":
      return <CheckCircleOutlined />;
    case "conversation":
      return <MessageOutlined />;
    case "document":
      return <FileTextOutlined />;
    case "ai":
      return <RobotOutlined />;
  }
}

function settled<T>(result: PromiseSettledResult<T>): T | undefined {
  return result.status === "fulfilled" ? result.value : undefined;
}

function percent(used = 0, quota = 0): number {
  if (quota <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((used / quota) * 100)));
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(value) / Math.log(1024)),
  );
  return `${new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: index === 0 ? 0 : 1,
  }).format(value / 1024 ** index)} ${units[index]}`;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("zh-CN").format(value);
}

function formatTime(value: Date): string {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(value);
}

function todayLabel(): string {
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
    weekday: "long",
  }).format(new Date());
}
